import { graphql, useStaticQuery } from "gatsby";
import { ContentTileProps } from "../components/elements/ContentTile/ContentTile";
import {
  CollectionPage,
  Image,
  KontentArticleOrSotDPage,
  StringValue,
} from "../types";
import { formatContentTiles } from "./contentTiles";
import { formatImages } from "./images";

export interface UseArticles {
  articles: Article[];
  pages: CollectionPage[];
}

export interface Article {
  page?: {
    url?: string;
    title?: string;
    displayPublishDate?: string;
  };
  systemId: string;
  image: Image;
  pageDescription?: string;
  contentCategory?: string;
  date?: string;
  contentTile?: ContentTileProps;
}

interface ArticleQuery {
  allKontentItemPage: {
    nodes: KontentArticleOrSotDPage[];
  };
  allPaginatedCollection: {
    nodes: {
      pages: CollectionPage[];
      pageCount: number;
    }[];
  };
}

export const formatArticle = (
  articleData: KontentArticleOrSotDPage["elements"]["content"]["value"][0]
) => {
  const contentTileData = articleData?.elements?.content_tile?.value;
  const contentTiles = contentTileData && formatContentTiles(contentTileData);

  const imageData = articleData?.elements?.image?.value[0]?.elements;
  const image = formatImages(imageData).largeImage;

  return {
    systemId: articleData?.system?.id,
    image: image,
    pageDescription:
      articleData?.elements?.page_metadata__page_description?.value,
    contentCategory:
      articleData?.elements.page_metadata__content_category?.value[0]?.name,
    contentTile: contentTiles && contentTiles[0],
  };
};

export const useArticles = (): UseArticles => {
  const articlesData = useStaticQuery<ArticleQuery>(graphql`
    query ArticleQuery {
      allKontentItemPage(
        filter: {
          elements: {
            content: {
              value: {
                elemMatch: { system: { type: { eq: "content___article" } } }
              }
            }
          }
        }
        sort: { elements: { display_publish_date: { value: DESC } } }
        limit: 33
      ) {
        nodes {
          id
          system {
            id
          }
          elements {
            content {
              value {
                ... on kontent_item_content___article {
                  id
                  elements {
                    image {
                      value {
                        ...kontentMediaImage
                      }
                    }
                    content_tile {
                      value {
                        ...kontentContentTile
                      }
                    }
                    page_metadata__content_category {
                      value {
                        name
                      }
                    }
                  }
                  system {
                    id
                  }
                }
              }
            }
            url {
              url
            }
            title {
              value
            }
            display_publish_date {
              value
            }
          }
        }
      }
      allPaginatedCollection(filter: { name: { eq: "articles" } }) {
        nodes {
          pages {
            id
            hasNextPage
            index
          }
          pageCount
        }
      }
    }
  `);

  if (articlesData.allKontentItemPage.nodes.length === 0) {
    return {
      articles: [],
      pages: [],
    };
  }

  const pages = articlesData.allPaginatedCollection?.nodes[0]?.pages.sort(
    (left, right) => left.index - right.index
  );

  const articles = articlesData.allKontentItemPage?.nodes.map((articlePage) => {
    const articleData = formatArticle(articlePage.elements?.content?.value[0]);

    const pageInfo = {
      url: articlePage.elements?.url?.url,
      title: articlePage.elements?.title?.value,
      displayPublishDate: articlePage.elements?.display_publish_date?.value,
    };

    return {
      page: pageInfo,
      ...articleData,
    };
  });

  return {
    articles,
    pages,
  };
};
